import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

const DISCOVERY_CALL_LINK = "https://docs.google.com/forms/d/e/1FAIpQLSffPqByMgvINjLjZurzxgeknKrY_jooGe_Co58QOYCNqHezZA/viewform";

export const PlayfulHero = () => {
  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFF9F0] via-[#FFF5E8] to-[#FFE8D6] px-6 py-32 relative overflow-hidden">
      {/* Playful background blobs */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#E07856]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-32 left-20 w-80 h-80 bg-[#40B5AD]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-[#F4B942]/10 rounded-full blur-2xl"></div>

      <div className="max-w-5xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E07856] to-[#F4B942] text-white px-6 py-2 rounded-full text-sm font-semibold mb-8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
          >
            <Sparkles size={16} />
            200+ Lives Transformed
          </motion.div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-[#1a1a1a] mb-6 leading-tight" style={{ fontFamily: "'Quicksand', sans-serif" }}>
            Become the version of you<br />
            <span className="text-[#E07856]">that you can't ignore.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#4a4a4a] mb-12 max-w-3xl mx-auto leading-relaxed">
            Ready to stop fixing yourself and start becoming the version of you<br className="hidden md:block" />
            that creates the life you actually want.
          </p>
          
          <motion.a
            href={DISCOVERY_CALL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#E07856] to-[#D45B3A] text-white px-10 py-5 rounded-full text-lg font-bold shadow-2xl shadow-[#E07856]/30 hover:shadow-[#E07856]/50 transition-all duration-300"
            whileHover={{ scale: 1.05, rotate: 1 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, type: "spring", bounce: 0.5 }}
          >
            Apply for Your Free Discovery Call
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </motion.div>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute bottom-10 right-10 w-24 h-24 bg-[#C4A7E7]/20 rounded-full"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-32 left-16 w-16 h-16 bg-[#F4B942]/20 rounded-full"
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
    </section>
  );
};

export const PlayfulCTA = ({ className = "" }) => {
  return (
    <div className={`text-center py-16 ${className}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.4 }}
        className="max-w-4xl mx-auto px-6"
      >
        <motion.a
          href={DISCOVERY_CALL_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-gradient-to-r from-[#E07856] to-[#D45B3A] text-white px-10 py-5 rounded-full text-lg font-bold shadow-2xl shadow-[#E07856]/30 hover:shadow-[#E07856]/50 transition-all duration-300 mb-6"
          whileHover={{ scale: 1.05, rotate: -1 }}
          whileTap={{ scale: 0.95 }}
        >
          Apply for Your Free Discovery Call
          <ArrowRight size={22} />
        </motion.a>
        
        <motion.p
          className="text-base md:text-lg text-[#5a5a5a] max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <span className="font-semibold text-[#E07856]">This isn't a casual chat</span> — it's for people ready to invest in real change, emotionally and financially. 
          If that's you, I'll personally review your application within 48 hours.
        </motion.p>
      </motion.div>
    </div>
  );
};
